import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { beehiveTheme } from '../../styles/beehiveTheme';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(15, 0),
  backgroundColor: '#fafafa',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '2.75rem',
  marginBottom: theme.spacing(4),
  color: beehiveTheme.primary.main,
  lineHeight: 1.2,
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem',
  },
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#4a5568',
  lineHeight: 1.9,
  marginBottom: theme.spacing(4),
}));

const StepContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const StepNumber = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: beehiveTheme.accent.main,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  fontWeight: 700,
}));

const StepContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const StepTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: beehiveTheme.primary.main,
  marginBottom: theme.spacing(1.5),
}));

const StepText = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  color: '#4a5568',
  lineHeight: 1.8,
}));

const TechNote = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: 'white',
  borderRadius: '8px',
  marginTop: theme.spacing(2),
  borderLeft: `4px solid ${beehiveTheme.accent.main}`,
}));

export default function HowItWorksSection() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const steps = [
    {
      title: 'Sensors collect data continuously',
      text: 'Cameras capture images of bees at the entrance. Microphones record hive sounds. Environmental sensors track temperature, humidity, and air quality. All of this happens automatically, around the clock.',
    },
    {
      title: 'AI processes everything on-device',
      text: 'A small computer at the hive (Raspberry Pi or Jetson Orin Nano) runs the AI models. It analyzes images for mites, listens for acoustic anomalies, and monitors environmental patterns—all in real-time without needing cloud connectivity.',
    },
    {
      title: 'Multiple signals are combined',
      text: "The system doesn't rely on any single metric. It fuses visual, acoustic, and environmental data together. This cross-checking makes detection more reliable and reduces false alarms.",
    },
    {
      title: 'Predictions are made and alerts sent',
      text: 'Machine learning models analyze trends and predict where mite populations are heading. If the system forecasts trouble, it alerts you immediately—typically weeks before traditional methods would catch the same issue.',
    },
  ];

  return (
    <SectionContainer id="how-it-works">
      <Container maxWidth="md">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Title>
            How does it actually work?
          </Title>

          <Paragraph>
            The technical details matter, but the basic idea is simple: constant monitoring + smart analysis = early warnings.
          </Paragraph>
        </motion.div>

        <Box sx={{ my: 6 }}>
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <StepContainer>
                <StepNumber>{index + 1}</StepNumber>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepText>{step.text}</StepText>
                </StepContent>
              </StepContainer>
            </motion.div>
          ))}
        </Box>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeIn}>
          <Paragraph sx={{ mt: 6 }}>
            The goal isn't just to build cool technology. It's to give beekeepers a practical tool that actually 
            helps them keep colonies alive. Early detection means more treatment options, better outcomes, and 
            less stress—for both beekeepers and bees.
          </Paragraph>
        </motion.div>
      </Container>
    </SectionContainer>
  );
}
